import { promises as fs } from "fs"
import path from "path"

const DATA_FILE_PATH = path.join(process.cwd(), "config", "data.json")

// Default offline data
const DEFAULT_OFFLINE_DATA = {
	isOffline: false,
	offlineMessage:
		"Maintenance en cours : Notre site est actuellement en mode de fonctionnement réduit en raison d'une panne de l'API. Nous travaillons pour restaurer toutes les fonctionnalités dès que possible. Veuillez nous excuser pour tout inconvénient.",
}

// Server-side only: Read the data.json file
export async function readOfflineData() {
	// Only run on server side
	if (typeof window !== "undefined") {
		console.warn("readOfflineData should only be called on server side")
		return DEFAULT_OFFLINE_DATA
	}

	try {
		const file = await fs.readFile(DATA_FILE_PATH, "utf8")
		const data = JSON.parse(file)
		return data
	} catch (error) {
		console.error("Error reading offline data:", error)
		return DEFAULT_OFFLINE_DATA
	}
}

// Server-side only: Update the data.json file
export async function updateOfflineData(data) {
	// Only run on server side
	if (typeof window !== "undefined") {
		console.warn("updateOfflineData should only be called on server side")
		return false
	}

	try {
		await fs.writeFile(DATA_FILE_PATH, JSON.stringify(data, null, 4))
		return true
	} catch (error) {
		console.error("Error updating offline data:", error)
		return false
	}
}

// Drupal health is probed at most once per TTL per Node process: called from every
// getServerSideProps, an uncached probe adds one Drupal round-trip per page view.
// Once a value is known, an expired one is served stale while the probe refreshes it.
const DRUPAL_STATUS_TTL_MS = 15 * 1000
let drupalStatusCache = null
let drupalStatusInFlight = null

const probeDrupalStatus = async () => {
	try {
		const response = await fetch(process.env.NEXT_PUBLIC_DRUPAL_BASE_URL + "/api", {
			method: "HEAD",
			signal: AbortSignal.timeout(5000),
		})
		return response.ok
	} catch (error) {
		console.error("Drupal site is down:", error)
		return false
	}
}

// Server-side only: Check if Drupal is accessible
export async function checkDrupalStatus() {
	// Only run on server side
	if (typeof window !== "undefined") {
		console.warn("checkDrupalStatus should only be called on server side")
		return true
	}

	if (drupalStatusCache && Date.now() < drupalStatusCache.expiresAt) {
		return drupalStatusCache.value
	}

	if (!drupalStatusInFlight) {
		drupalStatusInFlight = probeDrupalStatus()
			.then((value) => {
				drupalStatusCache = { value, expiresAt: Date.now() + DRUPAL_STATUS_TTL_MS }
				return value
			})
			.finally(() => {
				drupalStatusInFlight = null
			})
	}

	return drupalStatusCache ? drupalStatusCache.value : drupalStatusInFlight
}

// Server-side only: Update offline status
export async function updateOfflineStatus(isOffline) {
	const data = await readOfflineData()
	data.isOffline = isOffline
	return await updateOfflineData(data)
}

// Client-side safe: Get offline message from props or default
export function getOfflineMessage(offlineData = null) {
	if (offlineData && offlineData.offlineMessage) {
		return offlineData.offlineMessage
	}
	return DEFAULT_OFFLINE_DATA.offlineMessage
}
